import Icon from "@chakra-ui/icon";
import {
  Flex,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { RiCalendarLine } from "react-icons/ri";
import { CustomSelect } from "src/components/Inputs/CustomSelect";
import { ItemDivider } from "src/components/Inputs/CustomSelect/CustomSelect";
import { isNullOrUndefined } from "src/services/predicates";

export type DateRange =
  | { key: "LastDay" }
  | { key: "Last7Days" }
  | { key: "Last30Days" }
  | { key: "CustomLastDays"; days: string }
  | { key: "Today" }
  | { key: "Next7Days" }
  | { key: "Next30Days" }
  | { key: "CustomNextDays"; days: string }
  | { key: "CustomDateRange"; dateFrom: string; dateTo: string };

export type DateRangeItem = { key: DateRangeOption; label: string };

export type DateRangeOption =
  | "LastDay"
  | "Last7Days"
  | "Last30Days"
  | "CustomLastDays"
  | "Today"
  | "Next7Days"
  | "Next30Days"
  | "CustomNextDays"
  | "CustomDateRange";

export const DATE_RANGE_PAST_PREDEFINED_OPTIONS: DateRangeItem[] = [
  { key: "LastDay", label: "Today" },
  { key: "Last7Days", label: "Last 7 days" },
  { key: "Last30Days", label: "Last 30 days" },
];
export const DATE_RANGE_FUTURE_PREDEFINED_OPTIONS: DateRangeItem[] = [
  { key: "Today", label: "Today" },
  { key: "Next7Days", label: "Next 7 days" },
  { key: "Next30Days", label: "Next 30 days" },
];
export const DATE_RANGE_PAST_CUSTOM_OPTIONS: DateRangeItem[] = [
  { key: "CustomLastDays", label: "Custom last days" },
  { key: "CustomDateRange", label: "Custom date range" },
];
export const DATE_RANGE_FUTURE_CUSTOM_OPTIONS: DateRangeItem[] = [
  { key: "CustomNextDays", label: "Custom next days" },
  { key: "CustomDateRange", label: "Custom date range" },
];

export const PRESET_PAST_DAYS: Record<string, string> = {
  LastDay: "1",
  Last7Days: "7",
  Last30Days: "30",
};
export const PRESET_FUTURE_DAYS: Record<string, string> = {
  Today: "0",
  Next7Days: "7",
  Next30Days: "30",
};

export type DateRangeInputProps = {
  label: string;
  direction?: "past" | "future";
  selectedDateRange: DateRange | null;
  onChange: (value: DateRange | null) => void;
};

export const DateRangeInput = ({
  label,
  direction = "past",
  selectedDateRange,
  onChange,
}: DateRangeInputProps) => {
  const predefinedItems = useMemo(
    () =>
      direction === "future"
        ? DATE_RANGE_FUTURE_PREDEFINED_OPTIONS
        : DATE_RANGE_PAST_PREDEFINED_OPTIONS,
    [direction]
  );
  const customItems = useMemo(
    () =>
      direction === "future"
        ? DATE_RANGE_FUTURE_CUSTOM_OPTIONS
        : DATE_RANGE_PAST_CUSTOM_OPTIONS,
    [direction]
  );
  const allItems = useMemo(
    () => [...predefinedItems, ...customItems],
    [customItems, predefinedItems]
  );
  const customDaysKey =
    direction === "future" ? "CustomNextDays" : "CustomLastDays";

  const [customDays, setCustomDays] = useState(() => {
    if (
      selectedDateRange?.key !== "CustomLastDays" &&
      selectedDateRange?.key !== "CustomNextDays"
    ) {
      return "";
    }
    return selectedDateRange.days;
  });

  const [dateFrom, setDateFrom] = useState(() => {
    if (selectedDateRange?.key !== "CustomDateRange") return "";
    return selectedDateRange.dateFrom;
  });
  const [dateTo, setDateTo] = useState(() => {
    if (selectedDateRange?.key !== "CustomDateRange") return "";
    return selectedDateRange.dateTo;
  });

  const handleSelectChange = (item: DateRangeItem | null) => {
    if (isNullOrUndefined(item)) {
      onChange(null);
      return;
    }

    switch (item.key) {
      case "LastDay":
      case "Last7Days":
      case "Last30Days":
      case "Next7Days":
      case "Next30Days":
      case "Today": {
        onChange({ key: item.key });
        break;
      }
      case "CustomDateRange": {
        onChange({ key: item.key, dateFrom: dateFrom, dateTo: dateTo });
        break;
      }
      case "CustomLastDays": {
        onChange({ key: item.key, days: customDays });
        break;
      }
      case "CustomNextDays": {
        onChange({ key: item.key, days: customDays });
        break;
      }
      default:
        const _exhaustiveCheck: never = item.key;
        break;
    }
  };

  const selectedItem: DateRangeItem | null = useMemo(() => {
    if (isNullOrUndefined(selectedDateRange)) {
      return null;
    }

    return allItems.find((o) => o.key === selectedDateRange.key) ?? null;
  }, [allItems, selectedDateRange]);

  const handleCustomDaysChange = (value: string) => {
    setCustomDays(value);
    if (selectedItem?.key === customDaysKey) {
      onChange({ key: customDaysKey, days: value });
    }
  };

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    if (selectedItem?.key === "CustomDateRange") {
      onChange({ key: selectedItem.key, dateFrom: value, dateTo });
    }
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    if (selectedItem?.key === "CustomDateRange") {
      onChange({ key: selectedItem.key, dateFrom, dateTo: value });
    }
  };

  return (
    <Flex align="stretch" gap={2} direction="column">
      <CustomSelect<DateRangeItem>
        label={label}
        items={[...predefinedItems, ItemDivider, ...customItems]}
        selectedItem={selectedItem}
        onChange={handleSelectChange}
        renderItem={(item) => item.label}
        renderSelectedItem={(item) => item?.label ?? "Select date or range"}
        hideClearButton={false}
        leftIcon={<Icon as={RiCalendarLine} color="gray.400" />}
      />

      {selectedItem?.key === customDaysKey && (
        <Flex gap={3} align="center">
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            flexShrink={0}
            width="40px"
            textAlign="right"
          >
            {direction === "future" ? "Next" : "Last"}
          </Text>
          <NumberInput
            min={1}
            precision={0}
            value={customDays}
            size="md"
            onChange={(value) => handleCustomDaysChange(value)}
          >
            <NumberInputField placeholder="Eg.: 3" width="10rem" />
          </NumberInput>
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            flexShrink={0}
          >
            Days
          </Text>
        </Flex>
      )}

      {selectedItem?.key === "CustomDateRange" && (
        <Flex align="stretch" gap={2} wrap="wrap">
          <FormLabel flexGrow="1" width="10rem">
            Start date
            <Input
              type="date"
              size="md"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
            />
          </FormLabel>
          <FormLabel flexGrow="1" width="10rem">
            End date
            <Input
              type="date"
              size="md"
              placeholder="To"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
            />
          </FormLabel>
        </Flex>
      )}
    </Flex>
  );
};
